import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY, GOLD, PAYMENT_MODES } from '../utils/constants';

const PAYMENT_KEYS = ['razorpay_amount', 'cash_amount', 'icici_amount', 'bank_transfer_amount', 'gpay_amount'];

const todayInput = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const toDisplayDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
};

const labelStyle = { display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-ink-muted)', marginBottom: 4 };
const fieldStyle = { width: '100%', padding: '8px 12px', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', background: '#fff', color: NAVY };

// Records a payment against a booking via the record_booking_payment RPC.
// The RPC updates the mode column AND writes the ledger row in one
// transaction with the collection-trigger skip flag set, so exactly one
// ledger event lands with the staff-chosen date.
export default function RecordPaymentModal({ booking, onClose, onSaved }) {
  const net  = (Number(booking.total_amount) || 0) - (Number(booking.tac) || 0);
  const paid = PAYMENT_KEYS.reduce((s, k) => s + (Number(booking[k]) || 0), 0);
  const outstanding = net - paid;

  const [amount, setAmount] = useState(outstanding > 0 ? String(outstanding) : '');
  const [mode, setMode]     = useState('');
  const [date, setDate]     = useState(todayInput());
  const [note, setNote]     = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  // Existing ledger events for this booking, netted by date x mode (zero
  // nets hidden). Shown so staff know recording ADDS an event — it never
  // replaces one. Non-blocking by design.
  const [priorLines, setPriorLines] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('booking_collections')
        .select('collected_on, mode, amount')
        .eq('booking_id', booking.id)
        .order('collected_on');
      if (cancelled || !data) return;
      const byKey = new Map();
      data.forEach(ev => {
        const k = `${ev.collected_on}|${ev.mode}`;
        byKey.set(k, (byKey.get(k) || 0) + Number(ev.amount || 0));
      });
      setPriorLines(
        [...byKey.entries()]
          .map(([k, netAmt]) => {
            const [d, m] = k.split('|');
            return { date: d, mode: m, net: netAmt };
          })
          .filter(l => Math.abs(l.net) >= 0.5)
      );
    })();
    return () => { cancelled = true; };
  }, [booking.id]);

  const priorOtherDate = priorLines.some(l => l.date !== date);

  const handleSave = async () => {
    if (!amount || !mode || !date) { setError('Amount, mode and date are required.'); return; }
    const amt = Number(amount);
    if (isNaN(amt) || amt <= 0) { setError('Enter a valid amount.'); return; }
    if (date > todayInput()) { setError('Date cannot be in the future.'); return; }
    setSaving(true);
    const { error: err } = await supabase.rpc('record_booking_payment', {
      p_booking_id: booking.id,
      p_amount: amt,
      p_mode: mode,
      p_collected_on: date,
      p_note: note.trim() || null,
    });
    setSaving(false);
    if (err) { setError('Save failed: ' + err.message); return; }
    onSaved();
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 400, padding: '24px 24px 20px', boxShadow: '0 8px 40px rgba(10,22,40,0.22)' }}>
        <h3 style={{ margin: '0 0 2px', fontSize: '1rem', fontWeight: 700, color: NAVY }}>Record Payment</h3>
        <p style={{ margin: '0 0 14px', fontSize: '0.8rem', color: 'var(--color-ink-muted)' }}>{booking.booking_name}</p>

        {booking.partner_name && (
          <p style={{ margin: '0 0 14px', fontSize: '0.74rem', color: '#b45309', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '7px 10px', lineHeight: 1.4 }}>
            Partner booking ({booking.partner_name}) — for settlements, prefer the Partner
            Ledger so the payment allocates against the partner's account. Recording here is
            fine for a one-off payment on this booking alone.
          </p>
        )}

        <div style={{ display: 'flex', gap: 0, background: 'var(--color-sunken)', border: '1px solid var(--color-border)', borderRadius: 8, padding: '8px 0', marginBottom: 18 }}>
          {[
            { label: 'Net Total',   val: net },
            { label: 'Paid',        val: paid },
            { label: 'Outstanding', val: outstanding },
          ].map(({ label, val }, i) => (
            <div key={label} style={{ flex: 1, textAlign: 'center', borderLeft: i ? '1px solid var(--color-border)' : 'none' }}>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', color: 'var(--color-ink-muted)', marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: '0.88rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: label === 'Outstanding' ? (outstanding > 0 ? '#b45309' : '#15803d') : NAVY }}>
                {fmtMoney(val)}
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Amount (₹)</label>
          <input
            type="number" min="0" value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="0"
            autoFocus
            style={fieldStyle}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Payment Mode</label>
          <select value={mode} onChange={e => setMode(e.target.value)} style={fieldStyle}>
            <option value="">Select mode…</option>
            {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Collected On</label>
          <input
            type="date" value={date} max={todayInput()}
            onChange={e => setDate(e.target.value)}
            style={{ ...fieldStyle, colorScheme: 'light' }}
          />
        </div>

        {priorLines.length > 0 && (
          <div style={{ marginBottom: 14, border: '1px solid var(--color-border)', borderRadius: 8, padding: '8px 12px', background: 'var(--color-sunken)' }}>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', color: 'var(--color-ink-muted)', marginBottom: 4 }}>
              Already in ledger
            </div>
            {priorLines.map(l => (
              <div key={`${l.date}|${l.mode}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.76rem', color: NAVY, padding: '1px 0' }}>
                <span>{toDisplayDate(l.date)} — {l.mode}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: l.net < 0 ? '#dc2626' : NAVY }}>{fmtMoney(l.net)}</span>
              </div>
            ))}
            {priorOtherDate && (
              <p style={{ margin: '6px 0 0', fontSize: '0.72rem', color: '#b45309', lineHeight: 1.4 }}>
                Recording adds a new event — the entries above still stand and are not replaced.
                To move an existing payment to a different date, use Re-date in the Collections report
                instead of zeroing and re-recording.
              </p>
            )}
          </div>
        )}

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Note (optional)</label>
          <input
            type="text" value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="e.g. balance collected at boarding"
            style={fieldStyle}
          />
        </div>

        {error && <p style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: 14 }}>{error}</p>}

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={handleSave} disabled={saving}
            style={{ flex: 1, background: NAVY, color: GOLD, border: 'none', borderRadius: 8, padding: '9px 0', fontSize: '0.85rem', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}
          >
            {saving ? 'Saving…' : 'Record Payment'}
          </button>
          <button
            onClick={onClose}
            style={{ flex: 1, background: 'none', color: 'var(--color-ink-muted)', border: '1px solid var(--color-border-strong)', borderRadius: 8, padding: '9px 0', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
